import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useMediaPredicate } from 'react-media-hook';
import { MOBILE_SIZE } from 'utils/mediaQueryUtils';

import { usePageTitle } from 'utils/pageTitle';
import { getBackendSearchExcelExportUrl } from 'data/services/exportService';
import { recordSearch } from 'reducers/recordSearchReducer';
import Spinner from 'view/components/Spinner';
import { DATA_SET_OPTIONS, getLabelFromValue } from 'pages/backendSearch/backendSearchConstants';
import { DataSetSelectDesktop } from 'pages/backendSearch/DataSetSelect';
import BackendSearchTable from 'pages/backendSearch/BackendSearchTable';
import BackendSearchControls from 'pages/backendSearch/BackendSearchControls';
import './BackendSearchPage.css';

const BackendSearchPage = (props) => {
	usePageTitle('Backend Search');
	const dispatch = useDispatch();
	const isMobile = useMediaPredicate(MOBILE_SIZE);

	// Fix scrolling issue - ensure page can scroll
	useEffect(() => {
		// Target multiple possible parent containers
		const appBody = document.querySelector('.app-body');
		const appContainer = document.querySelector('.app');
		const html = document.documentElement;
		const body = document.body;

		// Fix app-body if it exists
		if (appBody) {
			appBody.classList.add('backend-search-active');
			appBody.style.height = 'auto';
			appBody.style.overflowY = 'auto';
			appBody.style.overflowX = 'hidden';
		}

		// Fix app container - override overflow: hidden
		if (appContainer) {
			appContainer.classList.add('backend-search-active');
			appContainer.style.height = 'auto';
			appContainer.style.overflow = 'visible';
			appContainer.style.overflowY = 'visible';
		}

		// Fix #root if it exists
		const root = document.getElementById('root');
		if (root) {
			root.classList.add('backend-search-active');
			root.style.height = 'auto';
			root.style.overflow = 'visible';
		}

		// Ensure html and body allow scrolling
		html.classList.add('backend-search-active');
		body.classList.add('backend-search-active');
		html.style.overflow = 'auto';
		html.style.overflowY = 'auto';
		html.style.height = 'auto';
		body.style.overflow = 'auto';
		body.style.overflowY = 'auto';
		body.style.height = 'auto';

		return () => {
			if (appBody) {
				appBody.classList.remove('backend-search-active');
				appBody.style.height = '';
				appBody.style.overflowY = '';
				appBody.style.overflowX = '';
			}
			if (appContainer) {
				appContainer.classList.remove('backend-search-active');
				appContainer.style.height = '';
				appContainer.style.overflow = '';
				appContainer.style.overflowY = '';
			}
			const root = document.getElementById('root');
			if (root) {
				root.classList.remove('backend-search-active');
				root.style.height = '';
				root.style.overflow = '';
			}
			html.classList.remove('backend-search-active');
			body.classList.remove('backend-search-active');
			html.style.overflow = '';
			html.style.overflowY = '';
			html.style.height = '';
			body.style.overflow = '';
			body.style.overflowY = '';
			body.style.height = '';
		};
	}, []);
	const {
		isLoading,
		data,
		dataSet,
		searchString,
	} = useSelector(state => ({
		isLoading: state.recordSearch.isLoading,
		data: state.recordSearch.data || [],
		dataSet: state.recordSearch.dataSet,
		searchString: state.recordSearch.searchString,
	}));

	const [selectedDataSet, setSelectedDataSet] = useState(DATA_SET_OPTIONS[0].value);
	const [searchTerm, setSearchTerm] = useState('');
	const [page, setPage] = useState(1);
	const [resultPage, setResultPage] = useState(1);

	const onSearch = (currentPage) => {
		const pageToSearch = currentPage || page;
		if (selectedDataSet && searchTerm) {
			setResultPage(pageToSearch);
			dispatch(recordSearch(selectedDataSet, searchTerm, currentPage || page));
		}
	};

	const onSelectDataSet = (e) => {
		setSelectedDataSet(e.target.value);
		setPage(1);
	};

	let exportLink = (
		<div className="backend-search-export">
			{data?.length ?
				<a className="export-link" href={getBackendSearchExcelExportUrl(dataSet, searchString, resultPage)}>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M14 10V14C14 14.5304 13.7893 15.0391 13.4142 15.4142C13.0391 15.7893 12.5304 16 12 16H4C3.46957 16 2.96086 15.7893 2.58579 15.4142C2.21071 15.0391 2 14.5304 2 14V10M5 7L8 10M8 10L11 7M8 10V2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
					</svg>
					Download Excel
				</a>
				: null
			}
		</div>
	);

	return (
		<div className="backend-search-page">
			{/* Hero Section */}
			<div className="backend-search-hero">
				<div className="backend-search-hero-content">
					<h1 className="backend-search-hero-title">Explore. Verify. Stay Compliant.</h1>
					<p className="backend-search-hero-subtitle">
						Bringing NYC property compliance into focus. Unified access to DOB, HPD, OATH, and other NYC records.
					</p>
				</div>
			</div>

			{/* Main Content */}
			<div className="backend-search-content">
				<div className={`backend-search-container ${isMobile ? 'mobile' : 'desktop'}`}>
					{!isMobile && (
						<div className="backend-search-sidebar">
							<DataSetSelectDesktop selectedDataSet={selectedDataSet} onSelect={onSelectDataSet} />
						</div>
					)}

					<div className="backend-search-main">
						<div className="backend-search-card">
							<div className="backend-search-card-header">
								<h2>Search Records</h2>
								<p>Select a dataset and enter your search term to find records</p>
							</div>
							<div className="backend-search-card-body">
								<BackendSearchControls
									onSelectDataSet={onSelectDataSet}
									onSearch={onSearch}
									page={page}
									setPage={setPage}
									searchTerm={searchTerm}
									selectedDataSet={selectedDataSet}
									setSearchTerm={setSearchTerm}
								/>
								{exportLink}
							</div>
						</div>

						{/* Results Section */}
						<div className="backend-search-results">
							{isLoading ? (
								<div className="backend-search-loading">
									<Spinner text={`Searching ${getLabelFromValue(selectedDataSet)} for "${searchTerm}"`} />
								</div>
							) : (
								<BackendSearchTable data={data} dataSet={dataSet} searchString={searchString} resultPage={resultPage} />
							)}
						</div>
					</div>
				</div>

				{/* Features Section */}
				{!isMobile && (
					<div className="backend-search-features">
						<div className="backend-search-feature-card">
							<div className="backend-search-feature-icon">
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
								</svg>
							</div>
							<h3>Advanced Search</h3>
							<p>Search across multiple NYC datasets with powerful filtering options</p>
						</div>
						<div className="backend-search-feature-card">
							<div className="backend-search-feature-icon">
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
								</svg>
							</div>
							<h3>Verified Data</h3>
							<p>Access verified records from official NYC agencies and departments</p>
						</div>
						<div className="backend-search-feature-card">
							<div className="backend-search-feature-icon">
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M4 7H20M4 12H20M4 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
								</svg>
							</div>
							<h3>Export Results</h3>
							<p>Download search results in Excel format for further analysis</p>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

export default BackendSearchPage;
